import { getWorkspaceMatchFulfilled, workspaceApi } from '@/api/workspace/index.ts';
import { initiateCheckCashByTradeCapture } from '@/store/cash/api/index.ts';
import { getAllCashProducts } from '@/store/cash/slice.ts';
import { initiateCheckFlexiForwardByTradeCapture } from '@/store/flexi-forward/api';
import { getAllFlexiForwardProducts } from '@/store/flexi-forward/slice.ts';
import { forwardAccumulatorApi } from '@/store/forwardAccumulator/api/index.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { optionSlice } from '@/store/option/slice.ts';
import { checkOptionThunk } from '@/store/option/thunks/checkOptionThunk.ts';
import { algoOrderApi } from '@/store/orders/algoOrders/api/index.ts';
import { mapFalcon, mapNightjar, mapTwap } from '@/store/orders/algoOrders/state.ts';
import { callOrderApi } from '@/store/orders/call/api/index.ts';
import { mapCallOrder } from '@/store/orders/call/state.ts';
import { fixingOrderApi } from '@/store/orders/fixing/api/index.ts';
import { mapFixing } from '@/store/orders/fixing/state.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { stopLossOrderApi } from '@/store/orders/stopLoss/api/index.ts';
import { mapStopLoss } from '@/store/orders/stopLoss/state.ts';
import { takeProfitOrderApi } from '@/store/orders/takeProfitOrder/api/index.ts';
import { mapTakeProfit } from '@/store/orders/takeProfitOrder/state.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { swapApi } from '@/store/swap/api/index.ts';
import { getAllSwapProducts } from '@/store/swap/slice.ts';
import { getSwapTradeCaptureQueue } from '@/store/swap/swapTradeCaptureQueue.ts';
import { targetAccumulatorApi } from '@/store/targetAccumulator/api/index.ts';
import { getAllTargetAccumulatorProducts } from '@/store/targetAccumulator/slice.ts';
import { selectTileProduct } from '@/store/tiles/selectors.ts';

// store.tabs.defaultClientId is initialized with 0 and set later from currentUser API.
// We fall back to the first saved tab's clientId when it is not yet populated.
function getClientId(tabsDefaultClientId: number, workspaceTabs: Array<{ clientId: number }> | undefined): number {
  if (tabsDefaultClientId !== 0) {
    return tabsDefaultClientId;
  }
  return workspaceTabs?.[0]?.clientId ?? 0;
}

function triggerTradeCaptureForAll(
  defaultClientId: number,
  { getState, dispatch }: { getState: () => AppState; dispatch: AppDispatch },
) {
  const state = getState();

  // CASH
  getAllCashProducts(state).forEach((product) => {
    if (!product.properties.currencyPair) {
      return;
    }
    dispatch(
      initiateCheckCashByTradeCapture({
        ...product,
        isNonDeliverable: product.type === 'FxNdf' ? true : undefined,
      }),
    );
  });

  // SWAP
  getAllSwapProducts(state).forEach((product) => {
    if (!product.properties.currencyPair) {
      return;
    }
    const { tradeCapture, ...productWithoutTradeCapture } = product;
    const { tileId } = productWithoutTradeCapture;
    const queue = getSwapTradeCaptureQueue(tileId);

    void queue.add(async () => {
      await dispatch(swapApi.endpoints.checkSwapByTradeCapture.initiate(productWithoutTradeCapture));
    });
  });

  // OPTION
  optionSlice.selectors.getAllOptionProducts(state).forEach((product) => {
    if (!product.properties.currencyPair) {
      return;
    }
    dispatch(
      checkOptionThunk({
        type: 'init',
        tileId: product.tileId,
        productName: product.type,
        initialProperties: product.properties,
        shouldApplyUserPrefs: false,
      }),
    );
  });

  getAllFlexiForwardProducts(state).forEach((product) => {
    if (!product.properties.currencyPair) {
      return;
    }
    dispatch(initiateCheckFlexiForwardByTradeCapture(product));
  });

  getAllTargetAccumulatorProducts(state).forEach((product) => {
    if (!product.properties.currencyPair) {
      return;
    }
    dispatch(targetAccumulatorApi.endpoints.checkTargetAccumulator.initiate(product));
  });

  forwardAccumulatorSlice.selectors.getAllForwardAccumulatorProducts(state).forEach((product) => {
    if (!product.properties.currencyPair) {
      return;
    }
    dispatch(forwardAccumulatorApi.endpoints.checkForwardAccumulator.initiate(product));
  });

  ordersSlice.selectors.getAllOrdersProducts(state).forEach((product) => {
    if (!product.properties.currencyPair) {
      return;
    }

    if (product.type === 'StopLossOrder') {
      dispatch(stopLossOrderApi.endpoints.validateStopLoss.initiate(mapStopLoss(product, defaultClientId)));
    }

    if (product.type === 'TakeProfitOrder') {
      dispatch(takeProfitOrderApi.endpoints.validateTakeProfit.initiate(mapTakeProfit(product, defaultClientId)));
    }

    if (product.type === 'FixingOrder') {
      dispatch(fixingOrderApi.endpoints.validateFixing.initiate(mapFixing(product, defaultClientId)));
    }

    if (product.type === 'CallOrder') {
      dispatch(callOrderApi.endpoints.validateCallOrder.initiate(mapCallOrder(product, defaultClientId)));
    }

    if (product.type === 'Twap+Order') {
      dispatch(algoOrderApi.endpoints.validateAlgo.initiate(mapTwap(product, defaultClientId)));
    }

    if (product.type === 'NightjarOrder') {
      dispatch(algoOrderApi.endpoints.validateAlgo.initiate(mapNightjar(product, defaultClientId)));
    }

    if (product.type === 'FalconOrder') {
      dispatch(algoOrderApi.endpoints.validateAlgo.initiate(mapFalcon(product, defaultClientId)));
    }
  });
}

export const registerTradeCaptureListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: getWorkspaceMatchFulfilled,
    // trigger trade capture call foreach product to populate them
    effect: (action, listenerApi) => {
      triggerTradeCaptureForAll(
        getClientId(listenerApi.getState().tabs.defaultClientId, action.payload?.tabs),
        listenerApi,
      );
    },
  });

  startAppListening({
    matcher: workspaceApi.endpoints.getSharedTab.matchFulfilled,
    effect: (action, { dispatch, getState }) => {
      const state = getState();
      const products =
        action.payload?.tiles.map((tile) => [tile.tileId, selectTileProduct(tile.tileId)(state)] as const) ?? [];
      products.forEach(([tileId, product]) => {
        if (!product?.properties.currencyPair) {
          return;
        }

        switch (product.type) {
          case 'FxSpot':
          case 'FxFwd':
            dispatch(initiateCheckCashByTradeCapture({ ...product, tileId, isNonDeliverable: undefined }));
            break;
          case 'FxNdf':
            dispatch(initiateCheckCashByTradeCapture({ ...product, tileId, isNonDeliverable: true }));
            break;
          case 'FxSwap': {
            const { tradeCapture, ...productWithoutTradeCapture } = product;
            const swapQueue = getSwapTradeCaptureQueue(tileId);

            void swapQueue.add(async () => {
              await dispatch(
                swapApi.endpoints.checkSwapByTradeCapture.initiate({ ...productWithoutTradeCapture, tileId }),
              );
            });
            break;
          }
          case 'Vanilla':
            dispatch(
              checkOptionThunk({
                type: 'init',
                tileId,
                productName: product.type,
                initialProperties: product.properties,
                shouldApplyUserPrefs: false,
              }),
            );
            break;
          case 'FxFlexiForward':
            dispatch(initiateCheckFlexiForwardByTradeCapture({ ...product, tileId }));
        }
      });
    },
  });
};
